import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, Eye, EyeOff, Loader2, XCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod/v3';
import { authApi } from '@/api/auth';
import { AnimatedCubeIcon } from '@/components/animated/animated-cube-icon';
import { isApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'A senha deve ter no mínimo 8 caracteres')
      .max(32, 'A senha deve ter no máximo 32 caracteres')
      .regex(/[A-Z]/, 'A senha deve conter ao menos uma letra maiúscula')
      .regex(/[a-z]/, 'A senha deve conter ao menos uma letra minúscula')
      .regex(/[0-9]/, 'A senha deve conter ao menos um número')
      .regex(
        /[!@#$%^&*()_\-+=[\]{};:'"\\|,.<>/?`~]/,
        'A senha deve conter ao menos um caractere especial',
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<'loading' | 'valid' | 'invalid'>(
    token ? 'loading' : 'invalid',
  );

  useEffect(() => {
    if (!token) return;

    authApi
      .verifyResetToken(token)
      .then(() => setTokenStatus('valid'))
      .catch(() => setTokenStatus('invalid'));
  }, [token]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    mode: 'onChange',
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password');

  const rules = useMemo(
    () => [
      {
        label: 'A senha deve ter no mínimo 8 caracteres.',
        isValid: password.length >= 8,
      },
      {
        label: 'A senha deve ter no máximo 32 caracteres.',
        isValid: password.length >= 8 && password.length <= 32,
      },
      {
        label: 'A senha deve conter uma letra minúscula.',
        isValid: /[a-z]/.test(password),
      },
      {
        label: 'A senha deve conter uma letra maiúscula.',
        isValid: /[A-Z]/.test(password),
      },
      {
        label: 'A senha deve conter ao menos um número.',
        isValid: /[0-9]/.test(password),
      },
      {
        label: 'A senha deve ter ao menos um caractere especial.',
        isValid: /[!@#$%^&*()_\-+=[\]{};:'"\\|,.<>/?`~]/.test(password),
      },
    ],
    [password],
  );

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;

    try {
      setError(null);

      await authApi.resetPassword(token, data.password);

      setSuccess(true);

      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      if (isApiError(err)) {
        setError(err.response?.data.message || 'Erro ao redefinir senha');
      } else {
        setError('Erro ao redefinir senha. Tente novamente.');
      }
    }
  };

  if (tokenStatus === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (tokenStatus === 'invalid') {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 py-12 lg:px-8 bg-gray-50">
        <div className="flex items-center justify-center w-full max-w-6xl h-[40rem] lg:h-[50rem] max-h-[90vh] p-20 rounded-xl border border-gray-200 bg-white text-gray-900 shadow">
          <div className="w-full flex flex-col lg:flex-row">
            <div className="hidden lg:flex flex-1 items-center justify-center relative">
              <div
                className={cn(
                  'absolute inset-0 rounded-l-xl',
                  '[background-size:40px_40px]',
                  '[background-image:linear-gradient(to_right,#d4d4d8_1px,transparent_1px),linear-gradient(to_bottom,#d4d4d8_1px,transparent_1px)]',
                )}
              />

              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)] rounded-l-xl" />

              <div className="relative text-center">
                <AnimatedCubeIcon size={60} className="mb-8 mx-auto" />
                <p className="bg-gradient-to-b from-gray-300 to-black bg-clip-text text-4xl font-bold text-transparent lg:text-5xl">
                  BCUBE
                </p>
                <p className="mt-4 text-gray-600">Recuperação de senha</p>
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-8">
              <div className="w-full max-w-md text-center">
                <div className="lg:hidden mb-6">
                  <AnimatedCubeIcon size={40} className="mx-auto" />
                </div>

                <h2 className="text-xl font-semibold text-gray-900 mb-2">Link expirado</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Este link de redefinição de senha não é mais válido. Solicite um novo link na
                  página de login.
                </p>

                <Link
                  to="/forgot-password"
                  className="inline-block rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
                >
                  Solicitar novo link
                </Link>

                <div className="mt-4">
                  <Link to="/login" className="text-sm text-blue-600 hover:underline">
                    Voltar para o login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12 lg:px-8 bg-gray-50">
      <div className="w-full max-w-6xl h-[40rem] lg:h-[50rem] max-h-[90vh] p-20 rounded-xl border border-gray-200 bg-white text-gray-900 shadow">
        <div className="flex flex-col lg:flex-row">
          <div className="hidden lg:flex flex-1 items-center justify-center relative">
            <div
              className={cn(
                'absolute inset-0 rounded-l-xl',
                '[background-size:40px_40px]',
                '[background-image:linear-gradient(to_right,#d4d4d8_1px,transparent_1px),linear-gradient(to_bottom,#d4d4d8_1px,transparent_1px)]',
              )}
            />

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)] rounded-l-xl" />

            <div className="relative text-center">
              <AnimatedCubeIcon size={60} className="mb-8 mx-auto" />
              <p className="bg-gradient-to-b from-gray-300 to-black bg-clip-text text-4xl font-bold text-transparent lg:text-5xl">
                BCUBE
              </p>
              <p className="mt-4 text-gray-600">Recuperação de senha</p>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-md">
              <div className="text-center px-0">
                <div className="lg:hidden mb-6 flex justify-center">
                  <AnimatedCubeIcon size={40} />
                </div>

                <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                  Crie sua nova senha
                </h1>
                <p className="mt-2 text-center text-sm text-gray-600">
                  Insira uma nova senha para sua conta
                </p>
              </div>

              <div className="px-0 mt-4">
                {error && (
                  <div
                    role="alert"
                    className="mb-6 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-600"
                  >
                    {error}
                  </div>
                )}

                {success && (
                  <output className="mb-6 block rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-600">
                    Senha redefinida com sucesso. Redirecionando para o login...
                  </output>
                )}

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="grid grid-cols-1 gap-x-6 gap-y-8">
                    <div>
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Senha
                      </label>
                      <div className="relative">
                        <input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          {...register('password')}
                          className={cn(
                            'flex h-9 w-full rounded-md border bg-transparent px-3 py-1 pr-10 text-sm shadow-sm transition-colors',
                            'placeholder:text-gray-400',
                            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            errors.password ? 'border-red-500' : 'border-gray-300',
                          )}
                        />
                        <button
                          type="button"
                          aria-label="Alternar visibilidade da senha"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <section aria-label="Regras de senha" className="space-y-1 text-sm">
                      {rules.map((rule) => (
                        <p
                          key={rule.label}
                          className={cn(
                            'flex items-center gap-2',
                            rule.isValid ? 'text-green-600' : 'text-red-600',
                          )}
                        >
                          {rule.isValid ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                          {rule.label}
                        </p>
                      ))}
                    </section>

                    <div>
                      <label
                        htmlFor="confirmPassword"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Confirme sua senha
                      </label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          {...register('confirmPassword')}
                          className={cn(
                            'flex h-9 w-full rounded-md border bg-transparent px-3 py-1 pr-10 text-sm shadow-sm transition-colors',
                            'placeholder:text-gray-400',
                            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            errors.confirmPassword ? 'border-red-500' : 'border-gray-300',
                          )}
                        />
                        <button
                          type="button"
                          aria-label="Alternar visibilidade da confirmação"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.confirmPassword.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting || success}
                      className={cn(
                        'inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium',
                        'bg-gray-900 text-white shadow',
                        'hover:bg-gray-800',
                        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400',
                        'disabled:pointer-events-none disabled:opacity-50',
                      )}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        'Redefinir senha'
                      )}
                    </button>
                  </div>

                  <div className="mt-4 text-center">
                    <Link to="/login" className="text-sm text-blue-600 hover:underline">
                      Voltar para o login
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
